import { useShopifyCurrency } from '../../common/currency/shopify-currency.hook';
import { IntervalType } from '../models/membership.model';
import type { Term } from '../models/membership.model';

/**
 * A non-renewing (One Time Purchase) term has no billing interval to show.
 * The backend models it as renewing=false AND a FOREVER interval; require both
 * so a feed that omits or misreports `renewing` alone can never flip a
 * recurring term to OTP (and drop its selling plan from the cart).
 */
export function isNonRenewingTerm(term: Term | undefined): boolean {
  return !!term && term.renewing === false && term.intervalType === IntervalType.FOREVER;
}

/**
 * Format currency amount using Shopify's currency settings
 * Replaces: ShopifyCurrencyPipe
 */
export function formatCurrency(amount: number): string {
  // Get currency from window.Shopify if available
  const rate = window.Shopify?.currency?.rate ?? 1;

  // Get currency symbol from theme settings
  let symbol = '$';
  if (window.theme?.moneyFormat) {
    symbol = window.theme.moneyFormat.split('{')[0];
    // Handle HTML entities in symbol
    if (symbol.indexOf('>') !== -1) {
      const div = document.createElement('div');
      div.innerHTML = symbol;
      symbol = div.innerText;
    }
  }

  // Convert amount based on currency rate
  const value = (amount / rate / 100.0).toFixed(2);

  // Format with thousands separator
  const parts = value.split('.');
  const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  const cents = parts[1] ? `.${parts[1]}` : '';

  return `${symbol}${dollars}${cents}`;
}

/**
 * Format currency with specific precision and separators
 * Used for Shopify money formatting with custom formats
 */
export function formatMoney(cents: number | string, format: string = '{{amount}}'): string {
  const symbol = window.Shopify?.currency?.active ?? 'USD';

  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /{{\s*(\w+)\s*}}/;
  const match = RegExp(placeholderRegex).exec(format);

  if (!match) {
    return formatCurrency(Number(cents));
  }

  let precision = 2;
  let thousands = ',';
  let decimal = '.';

  switch (match[1]) {
    case 'amount_no_decimals':
      precision = 0;
      break;
    case 'amount_with_comma_separator':
      thousands = '.';
      decimal = ',';
      break;
    case 'amount_no_decimals_with_comma_separator':
      precision = 0;
      thousands = '.';
      decimal = ',';
      break;
    case 'amount_with_apostrophe_separator':
      thousands = "'";
      break;
    case 'amount':
    default:
      // Use default values (precision = 2, thousands = ',', decimal = '.')
      break;
  }

  return formatWithDelimiters(Number(cents), precision, thousands, decimal) + ` ${symbol}`;
}

/**
 * Internal helper for formatting numbers with custom delimiters
 */
function formatWithDelimiters(
  value: number,
  precision: number = 2,
  thousands: string = ',',
  decimal: string = '.'
): string {
  if (isNaN(value) || value == null) {
    return '0';
  }

  const rate = window.Shopify?.currency?.rate ?? 1;
  const formatted = (value / rate / 100.0).toFixed(precision);

  const parts = formatted.split('.');
  const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
  const cents = parts[1] ? decimal + parts[1] : '';

  return dollars + cents;
}

/**
 * Format a date string
 * Replaces: DatePipe
 */
export function formatDate(date: string | Date, format: string = 'MMM d, yyyy'): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  // Simple implementation for common formats
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const fullMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const day = dateObj.getDate();
  const month = dateObj.getMonth();
  const year = dateObj.getFullYear();

  // Handle common format patterns
  if (format === 'MMM d, yyyy') {
    return `${months[month]} ${day}, ${year}`;
  } else if (format === 'MMMM d, yyyy') {
    return `${fullMonths[month]} ${day}, ${year}`;
  } else if (format === 'MM/dd/yyyy') {
    return `${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  } else if (format === 'yyyy-MM-dd') {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // Default format
  return `${months[month]} ${day}, ${year}`;
}

/**
 * Format membership term name (e.g., "month", "3 months", "year")
 * Replaces: MembershipTermNamePipe
 */
export function getMembershipTermName(
  term: Term | undefined,
  includeQuantityForSingleTerm: boolean = true
): string {
  if (!term) return '';

  let termName;

  if (term.intervalCount > 1) {
    // Multiple intervals: "3 months", "6 weeks"
    termName = `${term.intervalCount} ${term.intervalType}s`;
  } else if (includeQuantityForSingleTerm) {
    // Single interval with quantity: "1 month"
    termName = `${term.intervalCount} ${term.intervalType}`;
  } else {
    // Single interval without quantity: "month"
    termName = term.intervalType;
  }

  return termName.toLowerCase();
}

/**
 * Format term billing information (price + billing cycle)
 * Replaces: MembershipTermBillingPipe
 */
export function formatTermBilling(term: Term): string {
  const { shopifyCurrency } = useShopifyCurrency();
  const price = term.convertedPrice
    ? shopifyCurrency(term.convertedPrice, false)
    : shopifyCurrency(term.price);

  const termBilling =
    term.sellingPlanName && term.sellingPlanName !== ''
      ? term.sellingPlanName
      : `billed every ${getMembershipTermName(term, false)}`;

  return `${price} ${termBilling}`;
}

/**
 * Pluralize a word based on count
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return singular;
  return plural ?? `${singular}s`;
}

/**
 * Format a number with commas as thousands separator
 */
export function formatNumber(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Type declarations for window extensions
declare global {
  interface Window {
    Shopify?: {
      currency?: {
        active?: string;
        rate?: number;
      };
      designMode?: boolean;
    };
    theme?: {
      moneyFormat?: string;
    };
  }
}

/**
 * Humanize a string (capitalize first letter, replace underscores with spaces)
 */
export function humanize(str: string): string {
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}
